<template>
  <div class="demo-stage" data-test="demo-stage-bar">
    <div class="demo-stage__band">
      <!-- Left: the one real thing in a demo workspace — the signed-in
           person's own avatar and e-mail, plus the real workspace's name. An
           account without an address (issue 698) reads "Signed in" instead. -->
      <div class="demo-stage__real">
        <span class="demo-stage__self-ring">
          <Avatar :name="email || t('accountMenu.signedIn')" :src="selfPhotoUrl" size="md" self />
        </span>
        <span class="demo-stage__email" data-test="demo-stage-email">{{ email || t('accountMenu.signedIn') }}</span>
        <span v-if="workspaceName" class="demo-stage__workspace" data-test="demo-stage-workspace">· {{ workspaceName }}</span>
        <!-- A guest (issue 669) wears it for good: whose demo this is to her, said
             once and never hidden behind a menu. -->
        <span v-if="isGuest" class="demo-stage__guest" data-test="demo-stage-guest-marker">{{ t('demoStage.guestMarker') }}</span>
      </div>

      <!-- Middle: whose view this is — the fictional company and the
           two-half pill (worn role, that role's journey). -->
      <div v-if="stage" class="demo-stage__demo">
        <span class="demo-stage__chip">{{ t('demoStage.label') }}</span>
        <span class="demo-stage__company" data-test="demo-stage-bar-company">{{ companyName }}</span>
        <span class="demo-stage__sep" aria-hidden="true">|</span>
        <span class="demo-stage__viewing-as">{{ t('demoStage.viewingAs') }}</span>

        <div class="demo-stage__pill">
          <div class="demo-stage__pill-half" ref="personaRootRef">
            <button
              type="button"
              ref="personaTriggerRef"
              class="demo-stage__persona-btn"
              data-test="demo-stage-persona"
              aria-haspopup="true"
              :aria-expanded="personaPickerOpen"
              @click="togglePersonaPicker"
            >
              <template v-if="activePersona">
                <Avatar :name="personaName" :src="activePersona.photoUrl || undefined" size="md" />
                <span class="demo-stage__persona-name">{{ activePersona.firstName }} {{ activePersona.lastName }}</span>
                <span class="demo-stage__persona-role">{{ roleLabel(activePersona) }}</span>
              </template>
              <span v-else class="demo-stage__persona-placeholder">{{ t('demoStage.personaPlaceholder') }}</span>
              <ChevronIcon />
            </button>
            <div
              v-if="personaPickerOpen"
              ref="personaPanelRef"
              class="demo-stage__popover demo-stage__popover--picker"
              data-test="demo-stage-persona-picker"
              role="dialog"
              :aria-label="t('demoStage.picker.title')"
            >
              <h3 class="demo-stage__popover-title">{{ t('demoStage.picker.title') }}</h3>
              <p class="demo-stage__popover-subtitle">{{ t('demoStage.picker.subtitle', { count: personas.length, company: companyName }) }}</p>
              <ul class="demo-stage__persona-list">
                <li v-for="p in personas" :key="p.personId">
                  <button
                    type="button"
                    class="demo-stage__persona-option"
                    :class="{ 'demo-stage__persona-option--active': p.personId === activePersona?.personId }"
                    data-test="demo-stage-persona-option"
                    :data-person-id="p.personId"
                    @click="choosePersona(p.personId)"
                  >
                    <Avatar :name="`${p.firstName} ${p.lastName}`" :src="p.photoUrl || undefined" size="md" />
                    <span class="demo-stage__persona-option-text">
                      <span class="demo-stage__persona-option-name">
                        {{ p.firstName }} {{ p.lastName }}
                        <span class="demo-stage__persona-option-role">{{ roleLabel(p) }}</span>
                      </span>
                      <span v-if="personaDescription(p)" class="demo-stage__persona-option-desc">{{ personaDescription(p) }}</span>
                    </span>
                    <span class="demo-stage__persona-option-status">
                      <CheckIcon v-if="p.personId === activePersona?.personId" />
                      <template v-else-if="(progressFor(p.personId)?.done ?? 0) > 0">
                        <RingIcon :done="progressFor(p.personId)?.done ?? 0" :total="progressFor(p.personId)?.total ?? 0" :size="14" />
                        {{ progressFor(p.personId)?.done }} / {{ progressFor(p.personId)?.total }}
                      </template>
                      <template v-else>{{ t('demoStage.picker.taskCount', { total: taskTotalFor(p) }, taskTotalFor(p)) }}</template>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Reserved from the sheet before the journeys read answers (issue 494):
               the page reloads on every persona switch, and a half that only
               appeared once the read landed shifted the whole middle of the bar.
               Until then it holds its place, inert, with the count hidden. -->
          <div v-if="journey || pendingJourneyTotal" class="demo-stage__pill-half demo-stage__pill-half--journey" ref="journeyRootRef">
            <button
              type="button"
              ref="journeyTriggerRef"
              class="demo-stage__journey-btn"
              data-test="demo-stage-journey-pill"
              aria-haspopup="true"
              :aria-expanded="journeyCardOpen"
              :aria-busy="journey ? undefined : 'true'"
              :disabled="!journey"
              @click="toggleJourneyCard"
            >
              <RingIcon :done="journey?.done ?? 0" :total="journey?.total ?? pendingJourneyTotal" />
              <span :class="{ 'demo-stage__journey-count--pending': !journey }">{{ journey?.done ?? 0 }} / {{ journey?.total ?? pendingJourneyTotal }}</span>
              <ChevronIcon />
            </button>
            <div
              v-if="journeyCardOpen && journey"
              ref="journeyPanelRef"
              class="demo-stage__popover demo-stage__popover--journey"
              data-test="demo-stage-journey"
              role="dialog"
              :aria-label="t('demoStage.journey.title', { name: personaName })"
            >
              <div class="demo-stage__journey-head">
                <h3>{{ t('demoStage.journey.title', { name: personaName }) }}</h3>
                <span data-test="demo-stage-journey-count">{{ t('demoStage.journey.count', { done: journey.done, total: journey.total }) }}</span>
              </div>
              <div class="demo-stage__segments" aria-hidden="true">
                <span
                  v-for="task in journey.tasks"
                  :key="`seg-${task.key}`"
                  class="demo-stage__segment"
                  :class="`demo-stage__segment--${taskVisualState(task)}`"
                />
              </div>
              <ol class="demo-stage__tasks">
                <li
                  v-for="task in journey.tasks"
                  :key="task.key"
                  class="demo-stage__task"
                  :class="[
                    `demo-stage__task--${taskVisualState(task)}`,
                    { 'demo-stage__task--focused': task.key === focusedTaskKey },
                  ]"
                  role="button"
                  tabindex="0"
                  :aria-current="task.key === focusedTaskKey ? 'true' : undefined"
                  :aria-expanded="task.key === focusedTaskKey"
                  data-test="demo-stage-task"
                  :data-task="task.key"
                  :data-state="task.state"
                  @click="focusTask(task.key)"
                  @keydown.enter.prevent="focusTask(task.key)"
                  @keydown.space.prevent="focusTask(task.key)"
                >
                  <span class="demo-stage__task-mark" aria-hidden="true">
                    <CheckIcon v-if="task.state === 'done'" />
                    <BlockedIcon v-else-if="task.state === 'blocked'" />
                    <RingOutlineIcon v-else />
                  </span>
                  <div class="demo-stage__task-body">
                    <span v-if="task.key === nextTaskKey" class="demo-stage__task-next-label">{{ t('demoStage.journey.next') }}</span>
                    <span class="demo-stage__task-label">{{ taskLabel(task) }}</span>
                    <span v-if="task.state === 'blocked'" class="demo-stage__task-blocked" data-test="demo-stage-task-blocked">
                      {{ blockedReason(task) }}
                      <template v-if="canRebuild">
                        {{ t('demoStage.journey.blockedReset') }}
                        <Button variant="link" data-test="demo-stage-task-reset" @click.stop="askReset">{{ t('demoStage.reset') }}</Button>
                      </template>
                    </span>
                  </div>
                  <span class="demo-stage__task-module" data-test="demo-stage-task-module" :data-module="task.module">{{ moduleName(task.module) }}</span>
                  <a
                    v-if="task.key === focusedTaskKey && taskModuleHref(task)"
                    class="demo-stage__task-cta"
                    data-test="demo-stage-task-cta"
                    :href="taskModuleHref(task)!"
                    @click.stop="onOpenIn(task.key)"
                  >
                    {{ t('demoStage.journey.nextCta', { module: moduleName(task.module) }) }}
                  </a>
                  <!-- Lumi's own task has no module to link to: its CTA opens the
                       dock in place, on whatever page the person is on. -->
                  <Button
                    v-else-if="task.key === focusedTaskKey && task.module === 'assistant' && lumiDockLauncher.visible"
                    size="sm"
                    class="demo-stage__task-cta-button"
                    data-test="demo-stage-task-cta"
                    @click.stop="openLumi"
                  >
                    {{ t('demoStage.journey.nextCta', { module: moduleName(task.module) }) }}
                  </Button>
                </li>
              </ol>
              <span v-if="journeyUnknown" class="demo-stage__journey-line" data-test="demo-stage-journey-unknown">{{ t('demoStage.journey.unknown') }}</span>
              <span v-if="journey.complete && showYay" class="demo-stage__journey-yay" data-test="demo-stage-journey-yay">{{ t('demoStage.journey.yay', { name: personaName }) }}</span>
              <span v-if="journeysAnswer?.allComplete" class="demo-stage__journey-bridge" data-test="demo-stage-journey-bridge">
                {{ isAdmin ? t('demoStage.journey.bridgeAdmin') : t('demoStage.journey.bridgeUser') }}
              </span>
              <div class="demo-stage__journey-foot">
                <span>{{ t('demoStage.journey.footer') }}</span>
                <Button variant="ghost" size="sm" data-test="demo-stage-journey-refresh" :disabled="refreshing" @click="refreshJourneys">
                  <template #icon><RefreshIcon :spinning="refreshing" /></template>
                  {{ t('demoStage.journey.refresh') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: the way out (admins), sign-out (everyone), the rest behind the menu. -->
      <div class="demo-stage__actions">
        <!-- Invite a colleague (issue 669): the one door for real people in a demo,
             for the owner or a suite admin only — a guest or a plain member never
             sees it. Beside the rocket, folded to its icon at the same width. -->
        <button
          v-if="canInviteGuests"
          type="button"
          class="demo-stage__cta demo-stage__cta--invite"
          data-test="demo-stage-invite"
          :title="t('demoStage.guests.entry')"
          :aria-label="t('demoStage.guests.entry')"
          aria-haspopup="dialog"
          @click="openGuests"
        >
          <UserPlusIcon />
          <span class="demo-stage__cta-label">{{ t('demoStage.guests.entry') }}</span>
        </button>
        <!-- Reset is every member's (issue 669): the demo is fiction and rewinding it
             is no admin matter. A plain button beside the way out, folded to its
             icon like the invite entry — never a menu item only, never a danger. -->
        <button
          v-if="canRebuild"
          type="button"
          class="demo-stage__cta demo-stage__cta--reset"
          data-test="demo-stage-reset"
          :title="t('demoStage.reset')"
          :aria-label="t('demoStage.reset')"
          :disabled="busy"
          @click="askReset"
        >
          <RefreshIcon />
          <span class="demo-stage__cta-label">{{ t('demoStage.reset') }}</span>
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="demo-stage__cta"
          data-test="demo-stage-leave"
          :title="setupStarted ? t('demoStage.continueSetup') : t('demoStage.leave')"
          :aria-label="setupStarted ? t('demoStage.continueSetup') : t('demoStage.leave')"
          @click="redirect"
        >
          <RocketIcon />
          <span class="demo-stage__cta-label">{{ setupStarted ? t('demoStage.continueSetup') : t('demoStage.leave') }}</span>
        </button>
        <button
          type="button"
          class="demo-stage__icon-btn"
          data-test="demo-stage-signout"
          :aria-label="t('demoStage.signOut')"
          :title="t('demoStage.signOut')"
          @click="signOut"
        >
          <DoorIcon />
        </button>
        <Menu
          :items="menuItems"
          :label="t('demoStage.menu')"
          size="sm"
          class="demo-stage__menu"
          data-test="demo-stage-menu"
          @select="onMenu"
        />
      </div>
    </div>

    <!-- Surfaces when a reset silently fails server-side —
         always visible while true, independent of the intro/note row. -->
    <div v-if="rebuildFailed" class="demo-stage__rebuild-error" data-test="demo-stage-rebuild-error">
      {{ t('demoStage.rebuildFailed') }}
    </div>

    <!-- Under the band: the first-visit line (dismissed for good, per user and
         tenant) and/or an app's own note — same row family, never the
         permanent sentences the old bar carried. -->
    <div v-if="showIntro || note" class="demo-stage__below">
      <!-- A guest's first visit (issue 669): who let her in, that the company is
           made up and shared, and what Reset does for everyone. Her own card in
           place of the owner's intro, dismissed the same way. -->
      <div v-if="showIntro && isGuest" class="demo-stage__intro" data-test="demo-stage-guest-welcome">
        <InfoIcon class="demo-stage__intro-icon" />
        <p class="demo-stage__intro-text">
          <strong>{{ inviterLine }}</strong>
          {{ t('demoStage.guestWelcome.body', { company: companyName, reset: t('demoStage.reset') }) }}
        </p>
        <IconButton size="sm" variant="ghost" :label="t('demoStage.intro.close')" @click="dismissIntro">
          <CloseIcon />
        </IconButton>
      </div>
      <div v-else-if="showIntro" class="demo-stage__intro" data-test="demo-stage-intro">
        <InfoIcon class="demo-stage__intro-icon" />
        <p class="demo-stage__intro-text">
          <strong>{{ companyName }}</strong> {{ t('demoStage.intro.lead') }}
          {{ t('demoStage.intro.bridgeBefore') }}
          <!-- i18n-ignore: fixed product name -->
          <em>lumi HR</em>{{ t('demoStage.intro.bridgeAfter') }}
        </p>
        <IconButton size="sm" variant="ghost" :label="t('demoStage.intro.close')" @click="dismissIntro">
          <CloseIcon />
        </IconButton>
      </div>
      <div v-if="note" class="demo-stage__note" data-test="demo-stage-app-note">{{ note }}</div>
    </div>

    <!-- The barrier tape: the last strip before the app, so the demo frame ends
         in a line nobody mistakes for the real workspace. Decoration only. -->
    <div class="demo-stage__tape" aria-hidden="true" data-test="demo-stage-tape" />

    <!-- The confirmation names what is lost: everything the demo company holds
         now, including anything typed into it. It is also where the module set
         is chosen again (ADR 0083): the offer as the status serves it, the booked
         ones locked, the rest as the demo has them now. `confirm` outranks any
         modal a module has open under the bar. -->
    <Modal
      :open="pending"
      :title="t('demoStage.resetConfirm.title')"
      size="md"
      layer="confirm"
      :close-label="t('demoStage.cancel')"
      :dismissible="!busy"
      @close="cancel"
    >
      <p class="demo-stage__confirm-body" data-test="demo-stage-confirm-body">
        {{ t('demoStage.resetConfirm.body', { company: companyName }) }}
      </p>
      <DemoModulePicker v-if="isAdmin && moduleOffer.length" v-model="chosenModules" class="demo-stage__confirm-picker" :offer="moduleOffer" :disabled="busy" />
      <template #footer>
        <Button data-test="demo-stage-confirm-cancel" variant="secondary" :disabled="busy" @click="cancel">
          {{ t('demoStage.cancel') }}
        </Button>
        <Button data-test="demo-stage-confirm-cta" variant="danger" :loading="busy" :disabled="busy" @click="confirm">
          {{ t('demoStage.resetConfirm.cta') }}
        </Button>
      </template>
    </Modal>

    <!-- The colleagues of the demo. Mounted for admins only, and reads its roster
         when it is opened — the addresses never sit in the shared status. -->
    <DemoGuestsDialog v-if="guestsApi" :open="guestsOpen" :api="guestsApi" @close="closeGuests" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Avatar, Button, IconButton, Menu, Modal, lumiDockLauncher, openLumiDock } from '@lumi/ui';
import type { MenuItem, ModuleSwitchTarget } from '@lumi/ui';
import type { DemoJourneyModuleKey, DemoLabel, DemoLocale, DemoStage, DemoStageModuleKey, DemoStagePersona } from '../composables/useDemoPersonaState';
import type {
  DemoJourneyProgress,
  DemoJourneyTaskProgress,
  DemoJourneys,
  DemoJourneyTaskState,
  DemoOfferModule,
} from '../composables/useOnboardingStore';
import DemoGuestsDialog from './DemoGuestsDialog.vue';
import DemoModulePicker from './DemoModulePicker.vue';
import type { DemoGuestsApi } from '../composables/useDemoGuests';
import { USER_API_TO_LUMI, withDeepLink } from '../utils/moduleSwitcher';
import { demoStageIntroDismissed, dismissDemoStageIntro } from '../utils/demoStageIntro';
import { onDataChanged } from '../utils/dataChanged';
import { useAnchoredPopover } from '../composables/useAnchoredPopover';
import {
  BlockedIcon,
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  DoorIcon,
  InfoIcon,
  RefreshIcon,
  RocketIcon,
  UserPlusIcon,
} from './demoStageIcons';
import { RingIcon, RingOutlineIcon } from './demoStageRing';

// The demo bar (#409, ADR 0030 revision; redesigned per the "Demo-Leiste"
// canvas). Every module wears it above the whole app shell (AppShell `#top`)
// while the tenant is in demo. It answers two questions left to right: who am
// I (the real user, untouched by the demo) and whose view is this (the
// fictional company and the persona's role + journey, behind a two-half
// pill). `redirect` and `signOut` stay injected so app-kit is free of any
// app's store/runtime config; the persona choice goes through the shared
// onboarding store, which reloads the page on change.
const props = defineProps<{
  store: {
    isAdmin: boolean;
    // A member who is neither the owner nor an admin of a demo (issue 669): the
    // status says so about the caller only. The real workspace's company name is
    // what the guest's welcome names as the inviter's — never the fictional one.
    isGuest?: boolean;
    workspaceCompanyName?: string | null;
    setupStarted?: boolean;
    stage: DemoStage | null;
    activePersona: DemoStagePersona | null;
    setPersona: (value: string, leave?: () => void) => void;
    // The demo's module set and what a reset may choose from (ADR 0083).
    demoModules?: string[] | null;
    demoModuleOffer?: DemoOfferModule[];
    rebuildDemo?: (kind: 'reset', modules?: string[]) => Promise<{ ok: boolean }>;
    journeys?: DemoJourneys | null;
    fetchJourneys?: () => Promise<unknown>;
    celebrateJourney?: (personId: string) => Promise<unknown>;
    // The colleagues of the demo (issue 669): read, send, revoke. Without all
    // three the bar offers no invite entry.
    fetchDemoGuests?: DemoGuestsApi['fetchDemoGuests'];
    inviteDemoGuest?: DemoGuestsApi['inviteDemoGuest'];
    revokeDemoGuest?: DemoGuestsApi['revokeDemoGuest'];
  };
  redirect: () => void;
  signOut: () => void;
  // The real identity, from the app's auth/tenant stores. `userId`/`tenantSlug`
  // are what the first-visit dismissal is stamped against (never the e-mail);
  // without a `userId` the line still renders, just never persisted.
  // `photoUrl` is the REAL person's own People photo (issue 800) — never a worn
  // persona's; without one the chip keeps its initials.
  identity: {
    workspaceName?: string | null;
    email?: string | null;
    userId?: string | null;
    tenantSlug?: string | null;
    photoUrl?: string | null;
  };
  note?: string | null;
  // The same targets the app hands its own module switcher/LumiDock — carries
  // the session (token, locale, theme) AND the persona mask (ADR 0024, #409),
  // which a bar-local `buildModuleSwitchTargets({ tenantSlug })` call cannot:
  // the bar only ever holds `tenantSlug`. Without a matching target the next
  // task renders with no link at all, never a bare `href=""`.
  moduleTargets?: ModuleSwitchTarget[];
  // The module this bar sits in, as the sheet keys a persona's seats. A persona
  // chosen without a seat here is taken to Suite Home (issue 1056); absent (the
  // workspace, which every persona may open), every choice reloads in place.
  module?: DemoStageModuleKey;
}>();

const { t, te, locale } = useI18n();
const isAdmin = computed(() => props.store.isAdmin);
const isGuest = computed(() => props.store.isGuest === true && !props.store.isAdmin);
const note = computed(() => props.note || null);
const setupStarted = computed(() => props.store.setupStarted === true);
const stage = computed(() => props.store.stage);
const companyName = computed(() => stage.value?.companyName ?? '');
const workspaceName = computed(() => props.identity.workspaceName || '');
const email = computed(() => props.identity.email || '');
const selfPhotoUrl = computed(() => props.identity.photoUrl || undefined);
const personas = computed(() => stage.value?.personas ?? []);
const activePersona = computed(() => props.store.activePersona);
const personaName = computed(() => {
  const p = activePersona.value;
  return p ? `${p.firstName} ${p.lastName}` : '';
});

function roleLabel(persona: DemoStagePersona): string {
  const key = `demoStage.persona.${persona.key}`;
  return te(key) ? t(key) : persona.key;
}
function personaDescription(persona: DemoStagePersona): string {
  const key = `demoStage.personaDoes.${persona.key}`;
  return te(key) ? t(key) : '';
}

// --- Invite a colleague (issue 669) --------------------------------------------
// Offered to what the status calls an admin (the owner or a suite admin) when the
// app wired the three calls. A system operator also reads as admin here; user-api
// refuses an operator by name and the dialog answers it with a plain sentence.
const guestsApi = computed<DemoGuestsApi | null>(() => {
  const s = props.store;
  if (!isAdmin.value || !s.fetchDemoGuests || !s.inviteDemoGuest || !s.revokeDemoGuest) return null;
  return { fetchDemoGuests: s.fetchDemoGuests, inviteDemoGuest: s.inviteDemoGuest, revokeDemoGuest: s.revokeDemoGuest };
});
const canInviteGuests = computed(() => guestsApi.value !== null);
const guestsOpen = ref(false);
function openGuests() {
  closePersonaPicker(false);
  closeJourneyCard(false);
  guestsOpen.value = true;
}
function closeGuests() {
  guestsOpen.value = false;
}

// --- Reset (#409 M2; the module set, ADR 0083) --------------------------------
const canRebuild = computed(() => typeof props.store.rebuildDemo === 'function');
const moduleOffer = computed<DemoOfferModule[]>(() => props.store.demoModuleOffer ?? []);
const pending = ref(false);
const busy = ref(false);
const rebuildFailed = ref(false);
// The set the confirmation resets with. Seeded on every ask from the demo as it
// is now — a live demo without a stored set (pre-ADR 0083) had every module —
// and the picker keeps the locked ones in.
const chosenModules = ref<string[]>([]);

function askReset() {
  if (busy.value) return;
  // The Modal (`layer="confirm"`) always outranks a popover; close whichever
  // of ours is open FIRST, with focus returned to the pill half that opened
  // it — so when the Modal's own focus trap looks at `document.activeElement`
  // a moment later, it captures that half as the element to return to.
  closePersonaPicker(true);
  closeJourneyCard(true);
  rebuildFailed.value = false;
  const current = props.store.demoModules ?? moduleOffer.value.map((m) => m.key);
  chosenModules.value = moduleOffer.value.filter((m) => m.locked || current.includes(m.key)).map((m) => m.key);
  pending.value = true;
}
function cancel() {
  if (busy.value) return;
  pending.value = false;
}
async function confirm() {
  const run = props.store.rebuildDemo;
  if (!pending.value || !run || busy.value) return;
  busy.value = true;
  try {
    // No offer (a status from before ADR 0083) sends no set: the server keeps its own.
    // Only an admin chooses the set; a guest rebuilds the demo as it stands.
    const outcome = await run('reset', isAdmin.value && moduleOffer.value.length ? chosenModules.value : undefined);
    if (outcome.ok) return; // the page is leaving: the store reloads or routes on
    rebuildFailed.value = true;
    pending.value = false;
  } catch {
    rebuildFailed.value = true;
    pending.value = false;
  } finally {
    busy.value = false;
  }
}

// --- The persona picker popover ----------------------------------------------
const personaPickerOpen = ref(false);
const personaRootRef = ref<HTMLElement | null>(null);
const personaTriggerRef = ref<HTMLButtonElement | null>(null);
const personaPanelRef = ref<HTMLElement | null>(null);

function closePersonaPicker(returnFocus = true) {
  if (!personaPickerOpen.value) return;
  personaPickerOpen.value = false;
  if (returnFocus) personaTriggerRef.value?.focus();
}
function togglePersonaPicker() {
  if (personaPickerOpen.value) {
    closePersonaPicker();
    return;
  }
  closeJourneyCard(false);
  personaPickerOpen.value = true;
}
useAnchoredPopover(personaPickerOpen, personaRootRef, closePersonaPicker);

// Scopes the "open the journey once" flag to this tenant (sessionStorage: it
// must survive the reload `setPersona` triggers, but never leak to a
// different tab's first visit). Read once at setup so the immediate watcher
// below sees it before `onMounted` would otherwise be the first chance to.
const AUTO_OPEN_PREFIX = 'demoStageOpenJourneyOnce:';
function autoOpenJourneyKey(): string | null {
  const slug = props.identity.tenantSlug;
  return slug ? `${AUTO_OPEN_PREFIX}${slug}` : null;
}
const pendingAutoOpenJourney = ref(false);
{
  const key = autoOpenJourneyKey();
  if (key) {
    try {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        pendingAutoOpenJourney.value = true;
      }
    } catch {
      // Storage unavailable (private mode): the journey simply stays folded.
    }
  }
}

// #463: "Open in {module}" is always a full page load — every peer module lives
// on its own origin/path (moduleSwitcher.ts's peerBaseUrl), so there is no SPA
// route to navigate without unmounting this bar. The popover cannot literally
// "stay open" across that; what it CAN do is come back open, on the module the
// task pointed at, focused on that same task — the same sessionStorage relay
// `choosePersona` above already uses to survive its own reload.
const FOCUS_TASK_PREFIX = 'demoStageFocusTaskOnce:';
function focusTaskKey(): string | null {
  const slug = props.identity.tenantSlug;
  return slug ? `${FOCUS_TASK_PREFIX}${slug}` : null;
}
const pendingFocusTaskKey = ref<string | null>(null);
{
  const key = focusTaskKey();
  if (key) {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        sessionStorage.removeItem(key);
        pendingFocusTaskKey.value = stored;
        pendingAutoOpenJourney.value = true;
      }
    } catch {
      // Storage unavailable (private mode): the journey simply stays folded.
    }
  }
}

function choosePersona(personId: string) {
  const changing = personId !== activePersona.value?.personId;
  closePersonaPicker();
  if (!changing) return;
  const key = autoOpenJourneyKey();
  if (key) {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // Best effort — a missed flag just means the journey stays folded once.
    }
  }
  props.store.setPersona(personId, personaHomeLeave(personId));
}

// Reloaded in a module she holds no seat in, the module refuses the persona and
// the mask is dropped — the tab lands as nobody. She goes to Suite Home instead,
// through the app's own suite target (session + locale), wearing the new choice.
function personaHomeLeave(personId: string): (() => void) | undefined {
  if (!props.module) return undefined;
  const chosen = personas.value.find((p) => p.personId === personId);
  if (!chosen || chosen.modules[props.module]) return undefined;
  const href = props.moduleTargets?.find((target) => target.key === 'suite')?.href;
  if (!href) return undefined;
  const url = new URL(href);
  url.searchParams.set('demoPersona', personId);
  return () => window.location.assign(url.toString());
}

// --- The journey popover -------------------------------------------------
const journeyCardOpen = ref(false);
const journeyRootRef = ref<HTMLElement | null>(null);
const journeyTriggerRef = ref<HTMLButtonElement | null>(null);
const journeyPanelRef = ref<HTMLElement | null>(null);

function closeJourneyCard(returnFocus = true) {
  if (!journeyCardOpen.value) return;
  journeyCardOpen.value = false;
  if (returnFocus) journeyTriggerRef.value?.focus();
}
function toggleJourneyCard() {
  if (journeyCardOpen.value) {
    closeJourneyCard();
    return;
  }
  closePersonaPicker(false);
  journeyCardOpen.value = true;
}
useAnchoredPopover(journeyCardOpen, journeyRootRef, closeJourneyCard);

// --- The journeys (#409 M4) ---------------------------------------------------
const journeysAnswer = computed<DemoJourneys | null>(() => props.store.journeys ?? null);
function progressFor(personId: string): DemoJourneyProgress | null {
  return journeysAnswer.value?.journeys.find((j) => j.personId === personId) ?? null;
}
function taskTotalFor(persona: DemoStagePersona): number {
  return progressFor(persona.personId)?.total ?? persona.journey?.length ?? 0;
}
const journey = computed<DemoJourneyProgress | null>(() => {
  const personId = activePersona.value?.personId;
  if (!personId) return null;
  return progressFor(personId);
});
// The worn persona's task count as the sheet lists it, while the journeys read
// has not answered yet — 0 once it has, or when this app wires no journeys.
const pendingJourneyTotal = computed(() => {
  if (journey.value || !props.store.fetchJourneys) return 0;
  return activePersona.value?.journey?.length ?? 0;
});
watch(journey, (j) => {
  if (pendingAutoOpenJourney.value && j) {
    journeyCardOpen.value = true;
    pendingAutoOpenJourney.value = false;
  }
});
const journeyUnknown = computed(() => journey.value?.tasks.some((task) => task.state === 'unknown') === true);
const nextTaskKey = computed(() => journey.value?.tasks.find((task) => task.state === 'open')?.key ?? null);
function taskVisualState(task: DemoJourneyTaskProgress): DemoJourneyTaskState | 'next' {
  if (task.state === 'open' && task.key === nextTaskKey.value) return 'next';
  return task.state;
}

// --- Task focus (#463) -------------------------------------------------------
// The highlighted task (and, with it, which row shows "Open in …") is now a
// SEPARATE idea from `nextTaskKey`'s informational "NEXT" tag: clicking any
// task, finished or not, moves the highlight there. `nextTaskKey` keeps
// meaning exactly what it always has.
const focusedTaskKey = ref<string | null>(null);
function defaultFocusKey(j: DemoJourneyProgress | null): string | null {
  const tasks = j?.tasks ?? [];
  if (tasks.length === 0) return null;
  const firstOpen = tasks.find((task) => task.state === 'open');
  return firstOpen ? firstOpen.key : tasks[tasks.length - 1].key;
}
function focusTask(key: string) {
  focusedTaskKey.value = key;
}
// Every popover open (manual toggle, the post-persona-switch reopen, or the
// post-"Open in …" reopen below) resets focus to the default UNLESS a pending
// key survived the trip — never while it stays open (a poll landing mid-look
// must not yank the highlight out from under someone reading a task).
watch(journeyCardOpen, (isOpen) => {
  if (!isOpen) return;
  const j = journey.value;
  const pending = pendingFocusTaskKey.value;
  pendingFocusTaskKey.value = null;
  focusedTaskKey.value = pending && j?.tasks.some((task) => task.key === pending) ? pending : defaultFocusKey(j);
});
// The one exception to "never reset while open": the focused task itself
// disappearing from underneath the highlight (e.g. a rebuild resets the
// journey). A poll that leaves it in place changes nothing here.
watch(journey, (j) => {
  if (!journeyCardOpen.value || !j) return;
  if (focusedTaskKey.value && !j.tasks.some((task) => task.key === focusedTaskKey.value)) {
    focusedTaskKey.value = defaultFocusKey(j);
  }
});
// Stash the focused task before the actual navigation (the anchor's own
// default action) takes the page away, so the module that loads next — this
// same bar, freshly mounted — can reopen the popover on it.
function onOpenIn(key: string) {
  const storageKey = focusTaskKey();
  if (!storageKey) return;
  try {
    sessionStorage.setItem(storageKey, key);
  } catch {
    // Best effort — a missed flag just means the popover opens at its default.
  }
}

function openLumi() {
  // Focus goes into Lumi's panel, not back to the bar's trigger.
  closeJourneyCard(false);
  openLumiDock();
}

// The words on a task are its module's (ADR 0083 decision 6): the label and the
// blocked reason come with the journeys read, in the nine UI languages, and are
// shown in this UI's language, else English. app-kit holds no task label, reason
// label or task path of its own; a task from a stage stored before
// demo-cast.v5 shows its key, a blocked one the generic reason.
function localized(label: DemoLabel | null | undefined): string | undefined {
  if (!label) return undefined;
  const lang = String(locale.value).slice(0, 2).toLowerCase() as DemoLocale;
  return label[lang] || label.en || undefined;
}
function taskLabel(task: DemoJourneyTaskProgress): string {
  return localized(task.label) ?? task.key;
}
function moduleName(module: DemoJourneyModuleKey): string {
  // Lumi owns a task without being a module (no launcher tile, no registry entry):
  // it goes by its product name, like the dock does.
  if (module === 'assistant') return 'lumi';
  const lumiKey = USER_API_TO_LUMI[module];
  return lumiKey ? t(`lumiModules.${lumiKey}.name`) : module;
}
function blockedReason(task: DemoJourneyTaskProgress): string {
  return localized(task.reasonLabel) ?? t('demoStage.journey.blocked.gone');
}

// The "Open in {module}" link: reuses the app's OWN switcher targets
// (`moduleTargets`, ADR 0024) rather than building a URL here — the bar holds
// no token/locale/theme/persona-mask, so a link it built itself would land
// cross-origin signed out and without the worn persona. No matching target
// (prop not passed, or the module missing from it) means no link at all.
function taskModuleHref(task: DemoJourneyTaskProgress): string | null {
  const lumiKey = USER_API_TO_LUMI[task.module];
  if (!lumiKey) return null;
  const href = props.moduleTargets?.find((target) => target.key === lumiKey)?.href;
  if (!href) return null;
  // Where in its module the task is done (#537), as the module declared it: the
  // link lands there, not on the module's dashboard. No path: the module's start.
  return task.path ? withDeepLink(href, task.path) : href;
}

// The celebration: once per user and persona, on the read that first shows the
// journey complete.
const showYay = ref(false);
const celebrating = new Set<string>();
watch(
  () => [journey.value?.personId, journey.value?.complete, journey.value?.celebrated] as const,
  ([personId, complete, celebrated]) => {
    if (!personId || !complete) {
      showYay.value = false;
      return;
    }
    if (celebrated) return;
    if (celebrating.has(personId)) return;
    celebrating.add(personId);
    showYay.value = true;
    void props.store.celebrateJourney?.(personId);
  },
  { immediate: true },
);
watch(
  () => props.store.activePersona?.personId,
  () => {
    showYay.value = false;
  },
);

// Refresh while on screen: on mount, when the tab comes back, by hand, on the
// poll, and right after this tab's own writes (#460). One read at a time; a poll
// that lands during a read is skipped. The poll stays as the backstop for what
// this tab did not write itself (the worker, another user, Lumi server-side).
const JOURNEY_REFRESH_OPEN_MS = 10_000;
const JOURNEY_REFRESH_FOLDED_MS = 30_000;
// One save is often several requests; a trailing debounce answers them with one read.
const DATA_CHANGED_DEBOUNCE_MS = 400;
const refreshing = ref(false);
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let dataChangedTimer: ReturnType<typeof setTimeout> | null = null;
// A write that lands during a read is NOT skipped like a poll: that read may have
// been served before the write committed, so its tick would wait for the next
// poll. It earns exactly one more read once the current one settles.
let readAgain = false;
let stopDataChanged: (() => void) | null = null;
async function refreshJourneys() {
  if (!props.store.fetchJourneys || !props.store.activePersona || refreshing.value) return;
  refreshing.value = true;
  try {
    await props.store.fetchJourneys();
  } finally {
    refreshing.value = false;
    if (readAgain) {
      readAgain = false;
      void refreshJourneys();
    }
  }
}
function onOwnWrite() {
  if (dataChangedTimer) clearTimeout(dataChangedTimer);
  dataChangedTimer = setTimeout(() => {
    dataChangedTimer = null;
    if (refreshing.value) readAgain = true;
    else void refreshJourneys();
  }, DATA_CHANGED_DEBOUNCE_MS);
}
function onVisibility() {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') void refreshJourneys();
}
function schedulePoll() {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(() => void refreshJourneys(), journeyCardOpen.value ? JOURNEY_REFRESH_OPEN_MS : JOURNEY_REFRESH_FOLDED_MS);
}
onMounted(() => {
  void refreshJourneys();
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  stopDataChanged = onDataChanged(onOwnWrite);
  schedulePoll();
});
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
  stopDataChanged?.();
  if (dataChangedTimer) clearTimeout(dataChangedTimer);
  readAgain = false;
  if (refreshTimer) clearInterval(refreshTimer);
});
watch(journeyCardOpen, schedulePoll);
watch(() => props.store.activePersona?.personId, () => void refreshJourneys());

// --- The menu (#409 follow-ups, F2/F13) --------------------------------------
type MenuKey = 'leave' | 'reset' | 'sign_out';
const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [];
  const waiting = busy.value;
  if (isAdmin.value) {
    items.push({ key: 'leave', label: setupStarted.value ? t('demoStage.continueSetup') : t('demoStage.leave'), disabled: waiting });
  }
  // Every member may reset (issue 669); the visible button is the way in, the menu
  // keeps the entry beside the way out it has always sat with.
  if (canRebuild.value) items.push({ key: 'reset', label: t('demoStage.reset'), disabled: waiting });
  items.push({ key: 'sign_out', label: t('demoStage.signOut') });
  return items;
});
function onMenu(key: string) {
  switch (key as MenuKey) {
    case 'leave':
      props.redirect();
      break;
    case 'reset':
      askReset();
      break;
    case 'sign_out':
      props.signOut();
      break;
  }
}

// --- The first-visit line (dismissed for good, per user and tenant) ---------
// The keys themselves live in `utils/demoStageIntro`, because the operator's
// workspace reset has to be able to drop this dismissal too: a demo that was
// erased and seeded again is a first visit, whoever closed the line before.
// Who let a guest in. The status does not say (no field for it), so the line names
// the REAL workspace's company when the status carries it, else just "a colleague".
const inviterLine = computed(() => {
  const company = props.store.workspaceCompanyName?.trim();
  return company ? t('demoStage.guestWelcome.invitedBy', { workspace: company }) : t('demoStage.guestWelcome.invitedAnon');
});
const introDismissed = ref(demoStageIntroDismissed(props.identity.tenantSlug, props.identity.userId));
const showIntro = computed(() => !introDismissed.value);
function dismissIntro() {
  // Fail open on the cosmetic line: it closes for THIS tab either way, and is
  // only ever persisted when there is an owner to stamp it with.
  introDismissed.value = true;
  dismissDemoStageIntro(props.identity.tenantSlug, props.identity.userId);
}

</script>

<style scoped>
.demo-stage {
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: var(--font-body);
}
/* #459: a three-column grid, not a flex row — the middle block (who you're
   viewing as) must sit on the bar's true centre line whatever width the real
   identity on the left or the actions on the right happen to need, and only
   a grid with matching `1fr` side tracks does that regardless of either
   side's content length. `real` and `actions` are left free to size (and
   truncate/shrink) inside their own track; `actions` pins itself to the
   track's end so it hugs the bar's right edge rather than stretching into
   the generous 1fr track a long identity on the left would otherwise leave
   empty on the right. */
.demo-stage__band {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-areas: 'real demo actions';
  align-items: center;
  gap: 24px;
  /* Tall enough for an action whose label runs to two lines (issue 755): the
     right-hand buttons are 40px, so a label that wraps stays inside its button
     instead of spilling over the band's edge. */
  height: 60px;
  padding: 0 16px 0 20px;
  background: var(--band);
  color: var(--band-text);
  font-size: var(--text-sm);
}
.demo-stage__real {
  grid-area: real;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.demo-stage__self-ring {
  display: inline-flex;
  border-radius: var(--radius-pill);
  box-shadow: 0 0 0 1.5px var(--band-text);
}
[data-theme='dark'] .demo-stage__self-ring {
  /* Dark band is sand, the same hue as --avatar-self-bg: without an override
     the self avatar (and its ring) vanish into the band. Espresso ground with
     cream initials — reusing the band's own ink/surface pair — plus a solid
     lavender ring keep it legible (see finding 1). */
  box-shadow: 0 0 0 1.5px var(--info);
  --avatar-self-bg: var(--band-text);
  --avatar-self-fg: var(--band-surface);
}
.demo-stage__email {
  font-weight: var(--weight-semibold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.demo-stage__workspace {
  color: var(--band-muted-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.demo-stage__guest {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--band-text);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
}
.demo-stage__demo {
  grid-area: demo;
  display: flex;
  align-items: center;
  gap: 10px;
}
.demo-stage__chip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: var(--radius-pill);
  background: var(--band-text);
  color: var(--band);
  font-size: var(--text-caps);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.demo-stage__company {
  font-weight: var(--weight-semibold);
  white-space: nowrap;
}
.demo-stage__sep {
  color: var(--band-muted-text);
}
.demo-stage__viewing-as {
  color: var(--band-muted-text);
  white-space: nowrap;
}
.demo-stage__pill {
  display: flex;
  align-items: stretch;
  height: 32px;
  border-radius: var(--radius-pill);
  background: var(--band-surface);
  border: 1px solid var(--band-border);
  overflow: visible;
}
.demo-stage__pill-half {
  position: relative;
  display: flex;
}
.demo-stage__pill-half--journey {
  border-left: 1px solid var(--band-border);
}
.demo-stage__persona-btn,
.demo-stage__journey-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: var(--band-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  border-radius: var(--radius-pill);
}
.demo-stage__journey-btn:disabled {
  cursor: default;
}
.demo-stage__journey-count--pending {
  visibility: hidden;
}
.demo-stage__persona-btn:focus-visible,
.demo-stage__journey-btn:focus-visible,
.demo-stage__cta:focus-visible,
.demo-stage__icon-btn:focus-visible,
.demo-stage__persona-option:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.demo-stage__persona-role {
  color: var(--band-muted-text);
}
.demo-stage__persona-placeholder {
  color: var(--band-muted-text);
}
.demo-stage__actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
}
.demo-stage__cta,
.demo-stage__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--band-border);
  background: var(--band-surface);
  color: var(--band-text);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  cursor: pointer;
}
/* Toolbar-sized icons (issue 755): 18px like the menu trigger beside them, and
   never squeezed by a label that needs the room. */
.demo-stage__cta :deep(svg),
.demo-stage__icon-btn :deep(svg) {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}
.demo-stage__cta {
  padding: 0 12px;
}
.demo-stage__cta-label {
  /* One line while there is room; broken between words onto a second line,
     left-aligned beside the icon, when the band gets tight. */
  line-height: 1.2;
  text-align: left;
}
.demo-stage__icon-btn {
  width: 40px;
  padding: 0;
}

.demo-stage__cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.demo-stage__cta:hover:not(:disabled),
.demo-stage__icon-btn:hover {
  filter: brightness(1.08);
}
.demo-stage__menu {
  color: var(--band-text);
}
.demo-stage__menu :deep(.lumi-menu__trigger) {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--band-border);
  background: var(--band-surface);
  color: var(--band-text);
}
.demo-stage__menu :deep(.lumi-menu__trigger:hover),
.demo-stage__menu :deep(.lumi-menu__trigger--open) {
  filter: brightness(1.08);
  background: var(--band-surface);
  color: var(--band-text);
}

/* Popovers: white surface, panel-edge border, floating shadow, the popover
   layer (tokens/layers.css) — never a literal z-index. */
.demo-stage__popover {
  position: absolute;
  top: calc(100% + 8px);
  z-index: var(--layer-popover);
  background: var(--surface-card);
  border: 1px solid var(--panel-edge);
  border-radius: 14px;
  box-shadow: var(--shadow-lg);
  color: var(--text-body);
  font-size: var(--text-sm);
  padding: 16px;
}
.demo-stage__popover--picker {
  left: 0;
  width: 440px;
}
.demo-stage__popover--journey {
  right: 0;
  width: 420px;
}
.demo-stage__popover-title {
  margin: 0 0 4px;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.demo-stage__popover-subtitle {
  margin: 0 0 12px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
}
.demo-stage__persona-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 340px;
  overflow-y: auto;
}
.demo-stage__persona-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.demo-stage__persona-option:hover {
  background: var(--surface-wash);
}
.demo-stage__persona-option--active {
  background: var(--selected-bg);
}
.demo-stage__persona-option-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.demo-stage__persona-option-name {
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.demo-stage__persona-option-role {
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
  margin-left: 4px;
}
.demo-stage__persona-option-desc {
  color: var(--text-secondary);
  font-size: var(--text-xs);
}
.demo-stage__persona-option-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--info-text);
  font-size: var(--text-xs);
  white-space: nowrap;
}
.demo-stage__journey-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.demo-stage__journey-head h3 {
  margin: 0;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.demo-stage__segments {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}
.demo-stage__segment {
  flex: 1;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
}
.demo-stage__segment--done {
  background: var(--success);
}
.demo-stage__segment--next {
  background: var(--info);
}
.demo-stage__segment--blocked {
  background: var(--danger);
}
.demo-stage__tasks {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.demo-stage__task {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px;
  border-radius: 10px;
  cursor: pointer;
}
/* The highlight now follows the FOCUSED task (#463), which any click or
   Enter/Space moves — including onto an already-finished task — rather than
   the fixed "next open task" `--next` used to draw it. */
.demo-stage__task--focused {
  background: var(--selected-bg);
}
.demo-stage__task:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.demo-stage__task-mark {
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--text-tertiary);
}
.demo-stage__task--done .demo-stage__task-mark {
  color: var(--success);
}
.demo-stage__task--next .demo-stage__task-mark {
  color: var(--info);
}
.demo-stage__task--blocked .demo-stage__task-mark {
  color: var(--danger);
}
.demo-stage__task-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.demo-stage__task-next-label {
  font-size: var(--text-caps);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--info-text);
}
.demo-stage__task--done .demo-stage__task-label {
  text-decoration: line-through;
  color: var(--text-tertiary);
}
.demo-stage__task-blocked {
  color: var(--danger-text);
  font-size: var(--text-xs);
}
.demo-stage__task-module {
  flex-shrink: 0;
  align-self: center;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--pill-ring);
  background: var(--surface-wash);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  white-space: nowrap;
}
.demo-stage__task-cta {
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--btn-primary-bg);
  color: var(--btn-primary-fg);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  text-decoration: none;
  white-space: nowrap;
}
.demo-stage__task-cta-button {
  flex-shrink: 0;
  align-self: center;
}
.demo-stage__journey-line,
.demo-stage__journey-yay,
.demo-stage__journey-bridge {
  display: block;
  margin-bottom: 8px;
  font-size: var(--text-xs);
}
.demo-stage__journey-yay {
  font-weight: var(--weight-semibold);
  color: var(--success-text);
}
.demo-stage__journey-bridge {
  color: var(--info-text);
  font-weight: var(--weight-medium);
}
.demo-stage__journey-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--border-default);
  color: var(--text-secondary);
  font-size: var(--text-xs);
}
.demo-stage__spin {
  animation: demo-stage-spin 1s linear infinite;
}
@keyframes demo-stage-spin {
  to {
    transform: rotate(360deg);
  }
}

.demo-stage__rebuild-error {
  padding: 8px 20px;
  background: var(--danger-soft);
  border-bottom: 1px solid var(--panel-edge);
  color: var(--danger-text);
  font-size: var(--text-xs);
}
.demo-stage__tape {
  flex-shrink: 0;
  height: 10px;
  background: repeating-linear-gradient(-45deg, var(--attention) 0 8px, var(--info) 8px 16px);
}
.demo-stage__below {
  display: flex;
  flex-direction: column;
}
.demo-stage__intro {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 50px;
  padding: 10px 20px;
  background: var(--shell-panel);
  border-bottom: 1px solid var(--panel-edge);
  color: var(--text-body);
  font-size: var(--text-xs);
}
.demo-stage__intro-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--info);
}
.demo-stage__intro-text {
  flex: 1;
  margin: 0;
}
.demo-stage__intro-text strong {
  color: var(--text-heading);
}
.demo-stage__note {
  padding: 8px 20px;
  background: var(--shell-panel);
  border-bottom: 1px solid var(--panel-edge);
  color: var(--text-secondary);
  font-size: var(--text-xs);
}
.demo-stage__confirm-body {
  font-size: var(--text-sm);
  color: var(--text-muted, inherit);
}
.demo-stage__confirm-picker {
  margin-top: 16px;
}

/* Narrow widths (brief invariant): as the band tightens it drops labels IN
   THIS ORDER — "Ansicht als" (and the invite and reset entries' words), the workspace name, the "Demo beenden" label
   (icon only, tooltip via `title`), the company name. The pill, the DEMO
   chip, the avatar and the three right-hand controls never disappear. */
@media (max-width: 1280px) {
  .demo-stage__viewing-as {
    display: none;
  }
  /* The invite entry is the longest label of the three: it folds to its icon
     first (title and aria-label keep the words). */
  .demo-stage__cta--invite .demo-stage__cta-label,
  .demo-stage__cta--reset .demo-stage__cta-label {
    display: none;
  }
  /* Folded to its icon: a square button the same size as sign-out. */
  .demo-stage__cta--invite,
  .demo-stage__cta--reset {
    width: 40px;
    padding: 0;
  }
}
@media (max-width: 1180px) {
  .demo-stage__workspace {
    display: none;
  }
}
@media (max-width: 1100px) {
  .demo-stage__cta-label {
    display: none;
  }
  .demo-stage__cta {
    width: 40px;
    padding: 0;
  }
}
@media (max-width: 760px) {
  .demo-stage__company {
    display: none;
  }
}
@media (max-width: 480px) {
  /* Too narrow for three side-by-side blocks: `real` and `actions` keep the
     top row (identity left, way-out/sign-out right), `demo` — who you're
     viewing as — drops to its own full-width row below, same grouping the
     old flex-wrap produced, just named instead of relying on wrap order. */
  .demo-stage__band {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'real actions'
      'demo demo';
    height: auto;
    min-height: 60px;
    row-gap: 6px;
    padding: 6px 16px 6px 20px;
  }
  .demo-stage__demo {
    justify-self: stretch;
  }
}
</style>
